/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

// ToDo: remove casting where possible https://github.com/elastic/cloud/pull/125301#discussion_r1679467580

import { isEmpty, isEqual } from 'lodash'

import type { EuiComboBoxOptionOption } from '@elastic/eui'

import { makeSsoUrl } from '@modules/project-lib/kibanaLinks'
import type {
  OrganizationRoleAssignment,
  ProjectRoleAssignments,
  RoleAssignments,
} from '@modules/cloud-api/v1/types'
import type {
  NormalisedResourceRoleAssignment,
  NormalizedResource,
  Resource,
  ResourceRoleAssignment,
  ResourceRoleId,
  ResourceType,
  RoleAssignmentsTableItem,
  RoleBadgeIcon,
  RoleOptionDefinition,
  TableItemRoleDescriptor,
} from '@modules/role-assignments-lib/types'
import {
  ROLE_IDS,
  elasticsearchRoleIdsByLabel,
  isProjectResourceType,
  observabilityRoleIdsByLabel,
  resourceRoleIdsOrdering,
  resourceRoleLabelByRoleId,
  securityRoleIdsByLabel,
} from '@modules/role-assignments-lib/types'
import type { ProjectType } from '@modules/ui-types/projects'
import type { SecurityProductTier } from '@modules/project-user-api/v1/types'
import { ApiAccessOptions } from '@modules/role-assignments-components/ApiAccessSelector/types'

import {
  securityOptions,
  securityOptionsWithAiSocEngine,
  securityOptionsWithSearchAiLake,
} from './constants'

export const normaliseDeploymentRoleAssignments = (
  roleAssignments: Array<ResourceRoleAssignment<'deployment'>> = [],
): Array<NormalisedResourceRoleAssignment<'deployment'>> =>
  roleAssignments.filter(hasValidRoleId('deployment')).map((roleAssignment) => {
    if (roleAssignment.all) {
      return {
        ...roleAssignment,
        role_id: roleAssignment.role_id as ResourceRoleId<'deployment'>,
      }
    }

    return {
      ...roleAssignment,
      role_id: roleAssignment.role_id as ResourceRoleId<'deployment'>,
      ids: roleAssignment.deployment_ids?.map((id) => id),
    }
  })

export const normaliseCCMRoleAssignments = (
  roleAssignments: Array<ResourceRoleAssignment<'cloud-connected'>> = [],
): Array<NormalisedResourceRoleAssignment<'cloud-connected'>> =>
  roleAssignments.filter(hasValidRoleId('cloud-connected')).map((roleAssignment) => {
    if (roleAssignment.all) {
      return {
        ...roleAssignment,
        role_id: roleAssignment.role_id as ResourceRoleId<'cloud-connected'>,
      }
    }

    return {
      ...roleAssignment,
      role_id: roleAssignment.role_id as ResourceRoleId<'cloud-connected'>,
      ids: roleAssignment.resource_ids?.map((id) => id),
    }
  })

export const getProjectSpecificViewerRole = (
  projectType: ProjectType,
): ResourceRoleId<ProjectType> => {
  switch (projectType) {
    case 'elasticsearch':
      return elasticsearchRoleIdsByLabel.viewer
    case 'observability':
      return observabilityRoleIdsByLabel.viewer
    case 'security':
      return securityRoleIdsByLabel.viewer
    default:
      return elasticsearchRoleIdsByLabel.viewer
  }
}

export const normaliseProjectRoleAssignments =
  <T extends ProjectType>(projectType?: ProjectType) =>
  (
    roleAssignments: Array<ResourceRoleAssignment<T>> = [],
  ): Array<NormalisedResourceRoleAssignment<T>> =>
    roleAssignments.filter(notNoneRole()).map((roleAssignment) => {
      if (roleAssignment.all) {
        return {
          ...roleAssignment,
          role_id: roleAssignment.role_id as ResourceRoleId<T>,
        }
      }

      // Handle custom roles
      if (
        roleAssignment.application_roles?.length &&
        projectType &&
        hasCustomApplicationRoles(roleAssignment.application_roles, projectType)
      ) {
        return {
          ...roleAssignment,
          role_id: getProjectSpecificViewerRole(projectType) as ResourceRoleId<T>,
          ids: roleAssignment.project_ids?.map((id) => id),
          application_roles: roleAssignment.application_roles,
        }
      }

      // Handle pre-defined roles
      return {
        ...roleAssignment,
        role_id: roleAssignment.role_id as ResourceRoleId<T>,
        ids: roleAssignment.project_ids?.map((id) => id),
        application_roles: roleAssignment.application_roles,
      }
    })

export const flattenRoleAssignments = (
  roleAssignments: RoleAssignments,
): Array<NormalisedResourceRoleAssignment<ResourceType>> => [
  ...normaliseDeploymentRoleAssignments(roleAssignments.deployment).map((roleAssignment) => ({
    ...roleAssignment,
    resourceType: 'deployment' as ResourceType,
  })),
  ...(['elasticsearch', 'observability', 'security'] as ProjectType[]).flatMap((projectType) =>
    normaliseProjectRoleAssignments(projectType)(roleAssignments.project?.[projectType]).map(
      (roleAssignment) => ({
        ...roleAssignment,
        resourceType: projectType as ProjectType,
      }),
    ),
  ),
  ...normaliseCCMRoleAssignments(roleAssignments.cloud_connected_resource).map(
    (roleAssignment) => ({
      ...roleAssignment,
      resourceType: 'cloud-connected' as const,
    }),
  ),
]

export const getEsKibanaApiAccess = (
  roleAssignments: Array<NormalisedResourceRoleAssignment<ResourceType>>,
  resourceType: ResourceType,
  isApiKey?: boolean,
): ApiAccessOptions | undefined => {
  if (isProjectResourceType(resourceType)) {
    if (roleAssignments.length === 0) {
      return getDefaultEsKibanaApiAccessValue(isApiKey)
    }

    if (
      roleAssignments.some((roleAssignment) =>
        roleAssignmentHasPredefinedApplicationRoles(roleAssignment, resourceType, isApiKey),
      ) ||
      roleAssignments.every((roleAssignment) =>
        roleAssignmentHasCustomApplicationRoles(roleAssignment, resourceType),
      )
    ) {
      return ApiAccessOptions.CloudElasticsearchKibanaApi
    }

    return ApiAccessOptions.CloudApi
  }

  return undefined
}

export const getOrganizationOwnerApiAccessValue = (
  roleAssignment: OrganizationRoleAssignment,
  isApiKey?: boolean,
): ApiAccessOptions =>
  orgOwnerHasApplicationRoles(roleAssignment, isApiKey)
    ? ApiAccessOptions.CloudElasticsearchKibanaApi
    : ApiAccessOptions.CloudApi

export const getDefaultOrEmptyApiAccessValue = (
  resourceType: ResourceType,
  isApiKey?: boolean,
): ApiAccessOptions | undefined => {
  if (isProjectResourceType(resourceType)) {
    return getDefaultEsKibanaApiAccessValue(isApiKey)
  }

  return undefined
}

export const getDefaultEsKibanaApiAccessValue = (isApiKey?: boolean): ApiAccessOptions =>
  isApiKey ? ApiAccessOptions.CloudApi : ApiAccessOptions.CloudElasticsearchKibanaApi

export const roleAssignmentsToTableItems = <
  T extends ResourceType,
  R extends ResourceRoleAssignment<T>,
>(
  resourceType: T,
  resources: Array<Resource<T>> | undefined,
  roleAssignments: R[] = [],
  normaliseRoleAssignments: (roleAssignments: R[]) => Array<NormalisedResourceRoleAssignment<T>>,
  isApiKey?: boolean,
): Array<RoleAssignmentsTableItem<T>> => {
  if (resources === undefined) {
    return []
  }

  const normalisedRoleAssignments = normaliseRoleAssignments(roleAssignments)
  const allRoleAssignments = getAllRoleAssignments(normalisedRoleAssignments)

  if (allRoleAssignments.length > 0) {
    return resources.map((resource) => ({
      resourceType,
      id: resource.id,
      name: resource.name,
      roles: allRoleAssignments.map((allRole) => ({
        roleId: allRole.role_id,
      })),
      isDisabled: true,
      apiAccess: getEsKibanaApiAccess(allRoleAssignments, resourceType, isApiKey),
      ...('endpoints' in resource && {
        roleManagementUrl: makeSsoUrl(resource, {
          kibanaDeepLink: '/app/management/security/roles',
        }),
      }),
    }))
  }

  return specificRoleAssignmentsToTableItems(
    resourceType,
    resources,
    normalisedRoleAssignments,
    isApiKey,
  )
}

export const specificRoleAssignmentsToTableItems = <T extends ResourceType>(
  resourceType: T,
  resources: Array<Resource<T>> | undefined,
  normalisedRoleAssignments: Array<NormalisedResourceRoleAssignment<T>> = [],
  isApiKey?: boolean,
): Array<RoleAssignmentsTableItem<T>> => {
  if (resources === undefined) {
    return []
  }

  const specificRoleAssignments = filterRoleAssignments(
    normalisedRoleAssignments,
    'specificRoleAssignments',
  )

  const getSecurityProductTier = (resource: Resource<T>): SecurityProductTier | undefined => {
    if ('product_types' in resource && Array.isArray(resource.product_types)) {
      return resource.product_types[0]?.product_tier
    }

    return undefined
  }

  return resources.map((resource) => {
    const roleAssignments = specificRoleAssignments.filter(
      (specificRoleAssignment) => specificRoleAssignment.ids?.includes(resource.id) ?? false,
    )

    const emptyRoleAssignment: RoleAssignmentsTableItem<T> = {
      resourceType,
      id: resource.id,
      name: `${resource.name} (${resource.id.slice(0, 6)})`,
      productTier: getSecurityProductTier(resource),
      roles: [],
      isDisabled: false,
      apiAccess: getDefaultOrEmptyApiAccessValue(resourceType, isApiKey),
      ...('endpoints' in resource && {
        roleManagementUrl: makeSsoUrl(resource, {
          kibanaDeepLink: '/app/management/security/roles',
        }),
      }),
    }

    if (roleAssignments.length === 0) {
      return emptyRoleAssignment
    }

    const roles: TableItemRoleDescriptor[] = []

    const isApiAccess = getEsKibanaApiAccess(roleAssignments, resourceType, isApiKey)

    roleAssignments.forEach((roleAssignment) => {
      if (
        // Handle custom role
        isProjectResourceType(resourceType) &&
        roleAssignmentHasCustomApplicationRoles(roleAssignment, resourceType)
      ) {
        const applicationRoles = getApplicationRoles(roleAssignment) ?? [] // Just to appease TS
        roles.push(
          ...applicationRoles.map((applicationRole) => ({
            roleId: applicationRole,
            isCustomRole: true,
          })),
        )
      } else {
        // Predefined role
        roles.push({ roleId: roleAssignment.role_id })
      }
    })

    return { ...emptyRoleAssignment, roles, apiAccess: isApiAccess }
  })
}

export const deleteResourcesRoleAssignments = (
  roleAssignments: RoleAssignments,
): RoleAssignments => ({
  ...roleAssignments,
  deployment: getEmptyRoleAssignments().deployment,
  project: getEmptyRoleAssignments().project,
})

export const filterItemsByIdOrName = <T extends { id: string; name: string }>(
  items: T[],
  query: string,
): T[] =>
  items.filter((item) => {
    const lowerCasedQuery = query.toLocaleLowerCase().trim()

    return (
      item.id.toLocaleLowerCase().includes(lowerCasedQuery) ||
      item.name.toLocaleLowerCase().includes(lowerCasedQuery)
    )
  })

export const replaceAllDeploymentRoleAssignment = <T extends ResourceType>(
  organizationId: string,
  roles: Array<EuiComboBoxOptionOption<ResourceRoleId<T>>>,
  onChangeRoleAssignments: (roleAssignments: Array<ResourceRoleAssignment<'deployment'>>) => void,
): void =>
  onChangeRoleAssignments(
    roles.map((role) => ({
      organization_id: organizationId,
      role_id: role.value ?? '',
      all: true,
    })),
  )

export const replaceAllElasticsearchRoleAssignment = <T extends ResourceType>(
  organizationId: string,
  roles: Array<EuiComboBoxOptionOption<ResourceRoleId<T>>>,
  onChangeRoleAssignments: (
    roleAssignments: Array<ResourceRoleAssignment<'elasticsearch'>>,
  ) => void,
  apiAccessValue?: ApiAccessOptions,
  isApiKey?: boolean,
): void =>
  onChangeRoleAssignments(
    roles.map((role) => {
      const roleId = role.value ? (role.value as ResourceRoleId<'elasticsearch'>) : ''
      const roleLabel = resourceRoleLabelByRoleId[roleId] ?? roleId

      return {
        organization_id: organizationId,
        role_id: role.value ?? '',
        all: true,
        application_roles: getApplicationRolesForRoleAssignment(
          roleLabel,
          isApiKey,
          apiAccessValue,
        ),
      }
    }),
  )
export const replaceAllObservabilityRoleAssignment = <T extends ResourceType>(
  organizationId: string,
  roles: Array<EuiComboBoxOptionOption<ResourceRoleId<T>>>,
  onChangeRoleAssignments: (
    roleAssignments: Array<ResourceRoleAssignment<'observability'>>,
  ) => void,
  apiAccessValue?: ApiAccessOptions,
  isApiKey?: boolean,
): void =>
  onChangeRoleAssignments(
    roles.map((role) => {
      const roleId = role.value ? (role.value as ResourceRoleId<'observability'>) : ''
      const roleLabel = resourceRoleLabelByRoleId[roleId] ?? roleId

      return {
        organization_id: organizationId,
        role_id: role.value ?? '',
        all: true,
        application_roles: getApplicationRolesForRoleAssignment(
          roleLabel,
          isApiKey,
          apiAccessValue,
        ),
      }
    }),
  )
export const replaceAllSecurityRoleAssignment = <T extends ResourceType>(
  organizationId: string,
  roles: Array<EuiComboBoxOptionOption<ResourceRoleId<T>>>,
  onChangeRoleAssignments: (roleAssignments: Array<ResourceRoleAssignment<'security'>>) => void,
  apiAccessValue?: ApiAccessOptions,
  isApiKey?: boolean,
): void =>
  onChangeRoleAssignments(
    roles.map((role) => {
      const roleId = role.value ? (role.value as ResourceRoleId<'security'>) : ''
      const roleLabel = resourceRoleLabelByRoleId[roleId] ?? roleId

      return {
        organization_id: organizationId,
        role_id: role.value ?? '',
        all: true,
        application_roles: getApplicationRolesForRoleAssignment(
          roleLabel,
          isApiKey,
          apiAccessValue,
        ),
      }
    }),
  )
export const replaceAllCloudConnectedRoleAssignment = <T extends ResourceType>(
  organizationId: string,
  roles: Array<EuiComboBoxOptionOption<ResourceRoleId<T>>>,
  onChangeRoleAssignments: (
    roleAssignments: Array<ResourceRoleAssignment<'cloud-connected'>>,
  ) => void,
): void =>
  onChangeRoleAssignments(
    roles.map((role) => ({
      organization_id: organizationId,
      role_id: role.value ?? '',
      all: true,
    })),
  )

export const replaceAllRoleAssignment = <T extends ResourceType>(
  resourceType: T,
  organizationId: string,
  roles: Array<EuiComboBoxOptionOption<ResourceRoleId<T>>>,
  onChangeRoleAssignments: (roleAssignments: Array<ResourceRoleAssignment<T>>) => void,
  apiAccessValue?: ApiAccessOptions,
  isApiKey?: boolean,
): void => {
  switch (resourceType) {
    case 'deployment':
      replaceAllDeploymentRoleAssignment(organizationId, roles, onChangeRoleAssignments)
      break
    case 'elasticsearch':
      replaceAllElasticsearchRoleAssignment(
        organizationId,
        roles,
        onChangeRoleAssignments,
        apiAccessValue,
        isApiKey,
      )
      break
    case 'observability':
      replaceAllObservabilityRoleAssignment(
        organizationId,
        roles,
        onChangeRoleAssignments,
        apiAccessValue,
        isApiKey,
      )
      break
    case 'security':
      replaceAllSecurityRoleAssignment(
        organizationId,
        roles,
        onChangeRoleAssignments,
        apiAccessValue,
        isApiKey,
      )
      break
    case 'cloud-connected':
      replaceAllCloudConnectedRoleAssignment(organizationId, roles, onChangeRoleAssignments)
      break
    default:
      throw new Error(`Unhandled resourceType: ${resourceType}`)
  }
}

export const tableItemsToDeploymentRoleAssignment =
  (organizationId: string) =>
  (
    items: Array<RoleAssignmentsTableItem<'deployment'>>,
  ): Array<ResourceRoleAssignment<'deployment'>> => {
    const assignments: Array<ResourceRoleAssignment<'deployment'>> = []
    items.forEach((item) => {
      // Only one roles assignment is possible for deployments
      const roleId = item.roles.at(0)?.roleId

      if (roleId) {
        const found = assignments.find((assignment) => assignment.role_id === roleId)

        if (found) {
          found.deployment_ids?.push(item.id)
        } else {
          assignments.push({
            role_id: roleId,
            organization_id: organizationId,
            deployment_ids: [item.id],
          })
        }
      }
    })

    return assignments
  }

export const tableItemsToProjectRoleAssignment =
  (organizationId: string, isUiamEnabled?: boolean, isApiKey?: boolean) =>
  (
    items: Array<RoleAssignmentsTableItem<ProjectType>>,
  ): Array<ResourceRoleAssignment<ProjectType>> => {
    const projectType = items.at(0)?.resourceType ?? 'elasticsearch'

    const predefinedAssignments: Array<ResourceRoleAssignment<ProjectType>> = []
    const customAssignments: Array<ResourceRoleAssignment<ProjectType>> = []

    items.forEach((item) => {
      item.roles.forEach((role) => {
        if (role.isCustomRole) {
          const found = customAssignments.find((rA) => rA.project_ids?.includes(item.id))

          if (found) {
            found.application_roles?.push(role.roleId)
          } else {
            customAssignments.push({
              organization_id: organizationId,
              role_id: getProjectSpecificViewerRole(projectType),
              all: false,
              project_ids: [item.id],
              application_roles: [role.roleId],
            })
          }
        } else {
          const roleLabel = resourceRoleLabelByRoleId[role.roleId] ?? role.roleId
          const applicationRoles: string[] | undefined = isUiamEnabled
            ? getApplicationRolesForRoleAssignment(roleLabel, isApiKey, item.apiAccess)
            : undefined

          // separate predefined role assignments if application roles are different,
          // even if role_id is the same. This is to handle scenarios of two different resources
          // having the same role, but one has stack access and the other does not.
          const found = isUiamEnabled
            ? predefinedAssignments.find(
                (rA) =>
                  rA.role_id === role.roleId && isEqual(rA.application_roles, applicationRoles),
              )
            : predefinedAssignments.find((rA) => rA.role_id === role.roleId)

          if (found) {
            found.project_ids?.push(item.id)
          } else {
            predefinedAssignments.push({
              organization_id: organizationId,
              role_id: role.roleId,
              all: false,
              project_ids: [item.id],
              application_roles: applicationRoles,
            })
          }
        }
      })
    })

    return predefinedAssignments.concat(customAssignments)
  }

export const updateTableItemRoles = <T extends { id: string; roles: TableItemRoleDescriptor[] }, R>(
  items: T[],
  itemId: string,
  selectedRoleDescriptors: TableItemRoleDescriptor[],
  tableItemsToRoleAssignment: (items: T[]) => R[],
  onChangeRoleAssignments: (roleAssignments: R[]) => void,
  apiAccess?: ApiAccessOptions,
): void => {
  // Remove any invalid values
  const validRoleDescriptors = selectedRoleDescriptors.filter((role) => role.roleId.length > 0)
  const updateItems = items.map((item): T => {
    if (item.id === itemId) {
      return {
        ...item,
        apiAccess,
        roles: validRoleDescriptors.map((role) => ({
          roleId: role.roleId,
          isCustomRole: role.isCustomRole,
        })),
      }
    }

    return {
      // Remove any invalid or 'no roles" selection (empty strings), it is a UI selection only
      ...item,
      roles: item.roles.filter((role) => role.isCustomRole || role.roleId.length > 0),
    }
  })

  const roleAssignments = tableItemsToRoleAssignment(updateItems)

  onChangeRoleAssignments(roleAssignments)
}

export const setDeploymentRoleAssignments = (
  roleAssignments: RoleAssignments,
  deploymentRoleAssignments: Array<ResourceRoleAssignment<'deployment'>>,
  onChangeRoleAssignments: (roleAssignments: RoleAssignments) => void,
): void =>
  onChangeRoleAssignments({
    ...roleAssignments,
    deployment: deploymentRoleAssignments,
  })

export const setProjectRoleAssignments = <
  T extends ProjectType,
  R extends ResourceRoleAssignment<T>,
>(
  roleAssignments: RoleAssignments,
  projectType: T,
  projectRoleAssignments: R[],
  onChangeRoleAssignments: (roleAssignments: RoleAssignments) => void,
): void =>
  onChangeRoleAssignments({
    ...roleAssignments,
    ...{
      project: {
        ...roleAssignments.project,
        [projectType]: projectRoleAssignments,
      },
    },
  })

export const getAllRoleAssignments = <T extends ResourceType>(
  roleAssignments: Array<NormalisedResourceRoleAssignment<T>>,
): Array<NormalisedResourceRoleAssignment<T>> =>
  filterRoleAssignments(roleAssignments, 'allRoleAssignments')

export const getEmptyRoleAssignments = (): RoleAssignments => ({
  organization: [],
  deployment: [],
  project: {
    elasticsearch: [],
    observability: [],
    security: [],
  },
  cloud_connected_resource: [],
})

export const isRoleAssignmentsEmpty = (roleAssignments: RoleAssignments | undefined): boolean =>
  isEmpty(roleAssignments) ||
  (isEmpty(roleAssignments.organization) &&
    isStorageResourcesRoleAssignmentsEmpty(roleAssignments) &&
    isEmpty(roleAssignments.cloud_connected_resource))

// Storage resources are serverless projects and Elastic Cloud Hosted deployments as opposed to CCM, which is a
// cluster operations resource and has its own role assignments.
export const isStorageResourcesRoleAssignmentsEmpty = (
  roleAssignments: RoleAssignments | undefined,
): boolean =>
  isEmpty(roleAssignments?.deployment) &&
  isEmpty(roleAssignments?.project?.elasticsearch) &&
  isEmpty(roleAssignments?.project?.observability) &&
  isEmpty(roleAssignments?.project?.security)

export const filterRoleAssignments = <T extends ResourceType>(
  roleAssignments: Array<NormalisedResourceRoleAssignment<T>>,
  includeOnly:
    | 'allRoleAssignments'
    | 'specificRoleAssignments'
    | 'specificPredefinedRoleAssignments'
    | 'applicationRoleAssignments'
    | 'customApplicationRoleAssignments',
  isApiKey?: boolean,
): Array<NormalisedResourceRoleAssignment<T>> => {
  switch (includeOnly) {
    case 'allRoleAssignments':
    case 'specificRoleAssignments':
      const isAll = includeOnly === 'allRoleAssignments'

      return roleAssignments
        .filter((roleAssignment) => Boolean(roleAssignment.all) === isAll)
        .sort(sortRoleAssignments)
    case 'specificPredefinedRoleAssignments':
      return roleAssignments
        .filter(
          (roleAssignment) =>
            !roleAssignment.all &&
            !roleAssignmentHasCustomApplicationRoles(
              roleAssignment,
              roleAssignment.resourceType ?? 'allprojects',
            ),
        )
        .sort(sortRoleAssignments)
    case 'applicationRoleAssignments':
      return roleAssignments
        .filter(
          (roleAssignment) =>
            // We use doesApplicationRolesExist here intentionally.
            // In addition to non empty application roles arrays,
            // the result includes empty application roles [] array.
            // This is needed for the diff function to pick up user role
            // assignments that have been switched from undefined application
            // roles (cloud API + stack access) to empty application roles (cloud API only).
            !roleAssignment.all && doesApplicationRolesExist(roleAssignment, isApiKey),
        )
        .sort(sortRoleAssignments)
    case 'customApplicationRoleAssignments':
      return roleAssignments
        .filter(
          (roleAssignment) =>
            !roleAssignment.all &&
            roleAssignmentHasCustomApplicationRoles(
              roleAssignment,
              roleAssignment.resourceType ?? 'allprojects',
            ),
        )
        .sort(sortRoleAssignments)
    default:
      return []
  }
}

const hasValidRoleId =
  <T extends ResourceType>(resourceType: T) =>
  (roleAssignment: ResourceRoleAssignment<T>): roleAssignment is ResourceRoleAssignment<T> => {
    const roleIds: readonly string[] = ROLE_IDS[resourceType]
    return roleIds.includes(roleAssignment.role_id)
  }

const notNoneRole =
  <T extends ResourceType>() =>
  (roleAssignment: ResourceRoleAssignment<T>): roleAssignment is ResourceRoleAssignment<T> =>
    roleAssignment.role_id.length > 0

const sortRoleAssignments = <T extends ResourceType>(
  roleAssignment1: NormalisedResourceRoleAssignment<T>,
  roleAssignment2: NormalisedResourceRoleAssignment<T>,
): number =>
  resourceRoleIdsOrdering[roleAssignment1.role_id] -
  resourceRoleIdsOrdering[roleAssignment2.role_id]

export const resourceIconMap: Record<ResourceType, RoleBadgeIcon> = {
  elasticsearch: 'logoElasticsearch',
  observability: 'logoObservability',
  security: 'logoSecurity',
  deployment: 'logoElasticStack',
  'cloud-connected': 'link',
}

/**
 * This function filters out the deployments/projects that are no longer active from flattened role assignments.
 * If the role assignment is set to 'all', it will keep the assignment as is.
 * Else, it will filter the ids based on the userResourceList. If the ids are empty after filtering, the role assignment will be removed.
 */
export const filterActiveFlatRoleAssignments = ({
  flattenedRoleAssignments,
  userResourceList,
}: {
  flattenedRoleAssignments: Array<NormalisedResourceRoleAssignment<ResourceType>>
  userResourceList: NormalizedResource[]
}): Array<NormalisedResourceRoleAssignment<ResourceType>> => {
  const userResourceSet = new Set(
    userResourceList.map((resource) => `${resource.id}-${resource.resourceType}`),
  )
  return flattenedRoleAssignments
    .map((roleAssignment) => ({
      ...roleAssignment,
      ids: roleAssignment.ids?.filter((id) =>
        userResourceSet.has(`${id}-${roleAssignment.resourceType}`),
      ),
    }))
    .filter((roleAssignment) => roleAssignment.ids?.length || roleAssignment.all)
}

/**
 * Filters out deployments/projects/cloud-connected clusters that are no longer active
 * or have IDs that match specified filter criteria.
 *
 * Filtering behavior:
 * - Primary filtering: Removes resources with IDs matching the filter string
 * - Resource ID filtering: If filterDeletedResourceIds is provided, removes resources
 *   with IDs contained in that array
 * - Cloud-connected cluster handling: Used specifically for filtering inactive
 *   cloud-connected clusters when backend validation errors don't include field paths,
 *   requiring manual extraction of resource IDs from error messages
 *
 * Role assignment handling:
 * - If role assignment is set to 'all': keeps assignment unchanged
 * - Otherwise: filters IDs based on userResourceList and removes assignment
 *   if no valid IDs remain after filtering
 */
export const filterActiveRoleAssignments = ({
  roleAssignments,
  userResourceList,
  filterString,
  filterDeletedResourceIds,
}: {
  roleAssignments: RoleAssignments
  userResourceList?: NormalizedResource[]
  filterString?: string
  filterDeletedResourceIds?: string[]
}): RoleAssignments => {
  const filterSetOrString = userResourceList
    ? new Set(userResourceList.map((resource) => `${resource.id}-${resource.resourceType}`))
    : filterString || ''
  return {
    ...roleAssignments,
    deployment: filterResourceRoleAssignment({
      resourceRoleAssignments: roleAssignments.deployment,
      filterSetOrString,
      resourceType: 'deployment',
    }),
    project: filterProjectsHelper(roleAssignments.project, filterSetOrString),
    cloud_connected_resource: filterResourceRoleAssignment({
      resourceRoleAssignments: roleAssignments.cloud_connected_resource,
      filterSetOrString,
      resourceType: 'cloud-connected',
      filterDeletedResourceIds,
    }),
  }
}

// performs filterResourceRoleAssignment on nested project types (elasticsearch, security, observability)
// returns undefined if all project types' roles are undefined
const filterProjectsHelper = (
  projectRoleAssignments: ProjectRoleAssignments | undefined,
  filterSetOrString: Set<string> | string,
) => {
  if (projectRoleAssignments) {
    const filteredProject = {
      elasticsearch: filterResourceRoleAssignment({
        resourceRoleAssignments: projectRoleAssignments.elasticsearch,
        filterSetOrString,
        resourceType: 'elasticsearch',
      }),
      security: filterResourceRoleAssignment({
        resourceRoleAssignments: projectRoleAssignments.security,
        filterSetOrString,
        resourceType: 'security',
      }),
      observability: filterResourceRoleAssignment({
        resourceRoleAssignments: projectRoleAssignments.observability,
        filterSetOrString,
        resourceType: 'observability',
      }),
    }
    const allProjectsUndefined = !Object.values(filteredProject).some(
      (value) => value !== undefined,
    )
    return allProjectsUndefined ? undefined : filteredProject
  }

  return undefined
}

const getResourceIdParam = (resourceType: ResourceType) => {
  switch (resourceType) {
    case 'deployment':
      return 'deployment_ids'
    case 'elasticsearch':
      return 'project_ids'
    case 'security':
      return 'project_ids'
    case 'observability':
      return 'project_ids'
    case 'cloud-connected':
      return 'resource_ids'
    default:
      return ''
  }
}

const filterResourceRoleAssignment = ({
  resourceRoleAssignments,
  filterSetOrString,
  resourceType,
  filterDeletedResourceIds,
}: {
  resourceRoleAssignments: Array<ResourceRoleAssignment<ResourceType>> | undefined
  filterSetOrString: Set<string> | string
  resourceType: ResourceType
  filterDeletedResourceIds?: string[]
}): Array<ResourceRoleAssignment<ResourceType>> | undefined => {
  if (resourceRoleAssignments) {
    const resourceIdParam = getResourceIdParam(resourceType)

    const filterHelper = (id, filterParam, resourceTypeParam) => {
      if (filterDeletedResourceIds && filterDeletedResourceIds.length > 0) {
        return !filterDeletedResourceIds.includes(id)
      }

      return typeof filterParam === 'string'
        ? id !== filterParam
        : filterParam.has(`${id}-${resourceTypeParam}`)
    }

    const filteredRoleAssignments = resourceRoleAssignments
      .map((role: ResourceRoleAssignment<ResourceType>) => {
        if (role.all) {
          return role
        }

        return {
          ...role,
          [resourceIdParam]: role[resourceIdParam]?.filter((id: any) =>
            filterHelper(id, filterSetOrString, resourceType),
          ),
        }
      })
      .filter((role) => role[resourceIdParam]?.length || role.all)
    return filteredRoleAssignments.length ? filteredRoleAssignments : undefined
  }

  return undefined
}

export const isOrgOwnerRoleAssignments = ({
  roleAssignments,
  organizationId,
}: {
  roleAssignments: RoleAssignments | undefined
  organizationId: string
}): boolean =>
  roleAssignments?.organization?.some(
    (role) => role.role_id === 'organization-admin' && role.organization_id === organizationId,
  ) || false

export const getSecurityOptionsByProductTier = (
  productTier: SecurityProductTier | undefined,
): RoleOptionDefinition[] => {
  // TODO: replace with securityOptionsWithAiSocEngine when search_ai_lake is deprecated
  const defaultListOfRoleIds = [...securityOptions, ...securityOptionsWithSearchAiLake]
  return productTier
    ? {
        ai_soc_engine: securityOptionsWithAiSocEngine,
        search_ai_lake: securityOptionsWithSearchAiLake,
        essentials: securityOptions,
        complete: securityOptions,
      }[productTier]
    : defaultListOfRoleIds
}

function getApplicationRoles(
  roleAssignment: NormalisedResourceRoleAssignment<ResourceType> | OrganizationRoleAssignment,
): string[] | undefined {
  if ('application_roles' in roleAssignment) {
    return roleAssignment.application_roles as string[]
  }

  return undefined
}

export function doesApplicationRolesExist(
  roleAssignment: NormalisedResourceRoleAssignment<ResourceType>,
  isApiKey = true,
): boolean {
  const applicationRoles = getApplicationRoles(roleAssignment)

  return !!applicationRoles || !isApiKey
}

export function orgOwnerHasApplicationRoles(
  roleAssignment: OrganizationRoleAssignment,
  isApiKey = true,
): boolean {
  const applicationRoles = getApplicationRoles(roleAssignment)

  if (applicationRoles) {
    return applicationRoles.length > 0
  }

  // USER cloud API access = empty application roles array
  // USER cloud API + stack access = undefined OR explicitly filled application roles array
  // API KEYS cloud API access = undefined or empty application roles array
  // API KEYS cloud API + stack access = explicitly filled application roles array
  return !isApiKey
}

export function roleAssignmentHasPredefinedApplicationRoles(
  roleAssignment: NormalisedResourceRoleAssignment<ResourceType>,
  resourceType: ResourceType,
  isApiKey = true,
): boolean {
  const applicationRoles = getApplicationRoles(roleAssignment)

  if (applicationRoles) {
    return (
      applicationRoles.length > 0 && hasPredefinedApplicationRoles(applicationRoles, resourceType)
    )
  }

  // USER cloud API access = empty application roles array
  // USER cloud API + stack access = undefined OR explicitly filled application roles array
  // API KEYS cloud API access = undefined or empty application roles array
  // API KEYS cloud API + stack access = explicitly filled application roles array
  return !isApiKey
}

export function roleAssignmentHasCustomApplicationRoles(
  roleAssignment: NormalisedResourceRoleAssignment<ResourceType>,
  resourceType: ResourceType | 'allprojects' | undefined,
): boolean {
  const applicationRoles = getApplicationRoles(roleAssignment)

  return hasCustomApplicationRoles(applicationRoles, resourceType)
}

export function hasCustomApplicationRoles(
  applicationRoles: string[] | undefined,
  resourceType: ResourceType | 'allprojects' | undefined,
): boolean {
  switch (resourceType) {
    case 'elasticsearch':
      return (
        !!applicationRoles && applicationRoles.some((role) => !elasticsearchRoleIdsByLabel[role])
      )
    case 'observability':
      return (
        !!applicationRoles && applicationRoles.some((role) => !observabilityRoleIdsByLabel[role])
      )
    case 'security':
      return !!applicationRoles && applicationRoles.some((role) => !securityRoleIdsByLabel[role])
    case 'allprojects':
      return (
        !!applicationRoles &&
        applicationRoles.some(
          (role) =>
            !securityRoleIdsByLabel[role] &&
            !elasticsearchRoleIdsByLabel[role] &&
            !observabilityRoleIdsByLabel[role],
        )
      )
    default:
      return false
  }
}

export function hasPredefinedApplicationRoles(
  applicationRoles: string[] | undefined,
  resourceType: ResourceType | 'allprojects' | undefined,
): boolean {
  switch (resourceType) {
    case 'elasticsearch':
      return (
        !!applicationRoles && applicationRoles.some((role) => elasticsearchRoleIdsByLabel[role])
      )
    case 'observability':
      return (
        !!applicationRoles && applicationRoles.some((role) => observabilityRoleIdsByLabel[role])
      )
    case 'security':
      return !!applicationRoles && applicationRoles.some((role) => securityRoleIdsByLabel[role])
    case 'allprojects':
      return (
        !!applicationRoles &&
        applicationRoles.some(
          (role) =>
            securityRoleIdsByLabel[role] ||
            elasticsearchRoleIdsByLabel[role] ||
            observabilityRoleIdsByLabel[role],
        )
      )
    default:
      return false
  }
}

// [API KEY] Cloud Es+Kb API access: application roles = ['roleLabel']
// [API KEY]       Cloud API access: application roles = undefined OR []
// [USER]    Cloud Es+Kb API access: application roles = undefined OR ['roleLabel']
// [USER]          Cloud API access: application roles = []
export function getApplicationRolesForRoleAssignment(
  roleLabel: string,
  isApiKey?: boolean,
  apiAccess?: ApiAccessOptions,
): string[] | undefined {
  switch (apiAccess) {
    case ApiAccessOptions.CloudElasticsearchKibanaApi:
      return isApiKey ? [roleLabel] : undefined
    case ApiAccessOptions.CloudApi:
      return isApiKey ? undefined : []
    default:
      return undefined
  }
}
